/* Shared exporters for CSV / XLSX / PDF downloads.
 * Each helper takes an array of column definitions + an array of rows.
 *
 *   columns: [{ header: 'Date', key: 'date' }, ...]
 *   rows:    [{ date: '2026-06-01', ... }, ...]
 *
 * Every output carries Forge Ledger branding at the top.
 */
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

export const BRAND_NAME = 'Forge Ledger';
export const BRAND_TAGLINE = 'Forging a stronger financial future';
export const BRAND_LOGO_URL = 'https://customer-assets.emergentagent.com/job_budget-forecast-27/artifacts/w1b6b3hu_mini.png';

const sanitizeFileName = (name) => (name || 'export').replace(/[^a-z0-9_-]+/gi, '_').replace(/^_+|_+$/g, '');

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const todayLong = () => new Date().toLocaleString('en-GB', { dateStyle: 'long', timeStyle: 'short' });

const blobFromBuffer = (buffer, mime) => new Blob([buffer], { type: mime });

const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ----------------------------------------------------------------------
// CSV
// ----------------------------------------------------------------------
export const buildCSV = (columns, rows, { brandHeader = true, title = '' } = {}) => {
  const lines = [];
  if (brandHeader) {
    lines.push(csvEscape(BRAND_NAME));
    lines.push(csvEscape(BRAND_TAGLINE));
    if (title) lines.push(csvEscape(title));
    lines.push(csvEscape(`Exported ${todayLong()}`));
    lines.push('');
  }
  lines.push(columns.map((c) => csvEscape(c.header)).join(','));
  for (const row of rows) {
    lines.push(columns.map((c) => csvEscape(row[c.key])).join(','));
  }
  return lines.join('\r\n');
};

export const csvBlob = (columns, rows, opts = {}) => {
  const csv = buildCSV(columns, rows, opts);
  // BOM keeps Excel happy with UTF-8 characters (e.g. £)
  return new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
};

export const downloadCSV = (columns, rows, baseName = 'forge-ledger-export', title = '') => {
  triggerBlobDownload(csvBlob(columns, rows, { title }), `${sanitizeFileName(baseName)}.csv`);
};

// ----------------------------------------------------------------------
// XLSX
// ----------------------------------------------------------------------
export const xlsxBlob = (columns, rows, sheetName = 'Data', title = '') => {
  const aoa = [];
  aoa.push([BRAND_NAME]);
  aoa.push([BRAND_TAGLINE]);
  if (title) aoa.push([title]);
  aoa.push([`Exported ${todayLong()}`]);
  aoa.push([]);
  aoa.push(columns.map((c) => c.header));
  for (const row of rows) aoa.push(columns.map((c) => row[c.key] ?? ''));
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = columns.map((c) => ({ wch: Math.max(c.header.length + 2, c.width || 14) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, (sheetName || 'Data').slice(0, 31));
  const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return blobFromBuffer(buffer, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
};

export const downloadXLSX = (columns, rows, baseName = 'forge-ledger-export', sheetName = 'Data', title = '') => {
  triggerBlobDownload(xlsxBlob(columns, rows, sheetName, title), `${sanitizeFileName(baseName)}.xlsx`);
};

// ----------------------------------------------------------------------
// PDF — data table flavour with brand header
// ----------------------------------------------------------------------
const fetchLogoDataUrl = (() => {
  let cached = null;
  return async () => {
    if (cached) return cached;
    try {
      const res = await fetch(BRAND_LOGO_URL);
      const blob = await res.blob();
      cached = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      return cached;
    } catch (e) {
      return null;
    }
  };
})();

const drawBrandHeader = async (doc, title) => {
  const logo = await fetchLogoDataUrl();
  let leftX = 40;
  if (logo) {
    try {
      doc.addImage(logo, 'PNG', 40, 30, 36, 36);
      leftX = 88;
    } catch (e) {
      // PDF logo embed failed (corrupt image data) — exports still ship without the logo, so log and continue.
      console.warn('[exporters] brand logo embed failed:', e);
    }
  }
  doc.setFontSize(18);
  doc.setTextColor(2, 6, 23);
  doc.setFont('helvetica', 'bold');
  doc.text(BRAND_NAME, leftX, 50);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(BRAND_TAGLINE, leftX, 64);

  if (title) {
    doc.setFontSize(13);
    doc.setTextColor(2, 6, 23);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 40, 100);
  }
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(`Exported ${todayLong()}`, 40, title ? 116 : 90);

  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.5);
  doc.line(40, title ? 126 : 100, 555, title ? 126 : 100);
};

export const dataPdfBlob = async (columns, rows, title = '') => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  await drawBrandHeader(doc, title);
  autoTable(doc, {
    startY: title ? 140 : 114,
    head: [columns.map((c) => c.header)],
    body: rows.map((row) => columns.map((c) => {
      const v = row[c.key];
      return v === null || v === undefined ? '' : String(v);
    })),
    styles: { fontSize: 8, cellPadding: 4, textColor: [2, 6, 23] },
    headStyles: { fillColor: [0, 82, 255], textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 40, right: 40 },
    didDrawPage: (data) => {
      // Footer page-number on every page
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'center' }
      );
    },
  });
  const buffer = doc.output('arraybuffer');
  return blobFromBuffer(buffer, 'application/pdf');
};

export const downloadPDF = async (columns, rows, baseName, title) => {
  const blob = await dataPdfBlob(columns, rows, title);
  triggerBlobDownload(blob, `${sanitizeFileName(baseName)}.pdf`);
};

// ----------------------------------------------------------------------
// Screen-print PDF — html2canvas snapshot wrapped in a branded PDF.
// JPEG (quality 0.82) chosen over PNG to keep file size sane — at scale 1.6 a
// typical page exports under ~1.5 MB, easily fitting under email limits.
// ----------------------------------------------------------------------
export const screenPdfBlob = async (title = '') => {
  const target = document.querySelector('[data-print-area]') || document.querySelector('main') || document.body;
  document.body.classList.add('snapshot-mode');
  // Give the DOM a tick so any newly-displayed .print-only labels get laid out
  await new Promise((r) => requestAnimationFrame(r));

  // Capture "keep-together" zones (e.g. the prediction chart card) BEFORE
  // html2canvas runs, so we can later avoid slicing through them.
  const SCALE = 1.3;
  const targetRect = target.getBoundingClientRect();
  const keepTogetherZones = Array.from(document.querySelectorAll('[data-pdf-keep-together]')).map((el) => {
    const r = el.getBoundingClientRect();
    return {
      top: Math.max(0, (r.top - targetRect.top) * SCALE),
      bottom: Math.max(0, (r.bottom - targetRect.top) * SCALE),
    };
  }).filter((z) => z.bottom > z.top);

  let canvas;
  try {
    canvas = await html2canvas(target, {
      backgroundColor: '#ffffff',
      scale: SCALE,
      useCORS: true,
      logging: false,
      windowWidth: target.scrollWidth,
      windowHeight: target.scrollHeight,
      ignoreElements: (el) => el.matches && el.matches('.print-hide,[data-print-hide="true"]'),
    });
  } finally {
    document.body.classList.remove('snapshot-mode');
  }
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  await drawBrandHeader(pdf, title);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 40;
  const headerOffset = title ? 140 : 114;
  const usableHeight = pageHeight - headerOffset - margin;
  const usableWidth = pageWidth - margin * 2;

  // Scale screenshot to width
  const imgWidth = usableWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let position = headerOffset;
  let sourceY = 0;
  // Each slice fills as much of a single A4 page as possible.
  const sliceHeightPx = (usableHeight / imgHeight) * canvas.height;

  while (sourceY < canvas.height) {
    let sliceH = Math.min(sliceHeightPx, canvas.height - sourceY);
    if (sliceH < 1) break;  // safety net against floating-point edges

    // If this slice would split a keep-together zone, shrink it so the slice
    // ends at the zone's top edge — that zone will then start on the next page.
    // BUT only if the shrink is modest (we'd rather split a card than waste
    // half a page). Threshold: shrink only if at least 60% of the page would
    // still be filled with content.
    const sliceEnd = sourceY + sliceH;
    const minAcceptableSliceH = sliceHeightPx * 0.6;
    for (const zone of keepTogetherZones) {
      const zoneFits = (zone.bottom - zone.top) <= sliceHeightPx;
      const wouldSplit = zone.top > sourceY && zone.top < sliceEnd && zone.bottom > sliceEnd;
      if (zoneFits && wouldSplit) {
        const newSliceH = Math.floor(zone.top - sourceY);
        if (newSliceH > minAcceptableSliceH) {
          sliceH = newSliceH;
          break;
        }
        // else: leave the slice as-is and accept the split — better than
        // wasting most of the page.
      }
    }
    // Crop a slice the size of one page from the source canvas
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = sliceH;
    const ctx = slice.getContext('2d');
    // Paint white background first so JPEG doesn't darken transparent pixels
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, sourceY, slice.width, slice.height, 0, 0, slice.width, slice.height);
    const sliceData = slice.toDataURL('image/jpeg', 0.82);
    const sliceHeightPt = (slice.height * imgWidth) / canvas.width;
    pdf.addImage(sliceData, 'JPEG', margin, position, imgWidth, sliceHeightPt);
    sourceY += slice.height;
    if (sourceY < canvas.height) {
      pdf.addPage();
      await drawBrandHeader(pdf, title);
      position = headerOffset;
    }
  }
  return blobFromBuffer(pdf.output('arraybuffer'), 'application/pdf');
};

export const downloadScreenPDF = async (baseName, title) => {
  const blob = await screenPdfBlob(title);
  triggerBlobDownload(blob, `${sanitizeFileName(baseName)}.pdf`);
};

// ----------------------------------------------------------------------
// Helper: read blob as base64 (for emailing attachments)
// ----------------------------------------------------------------------
export const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    const result = reader.result || '';
    const comma = result.indexOf(',');
    resolve(comma >= 0 ? result.slice(comma + 1) : result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});
